import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface CombatEntity {
  id: string;
  name: string;
  hp: number;
  maxHp: number;
  mana: number;
  maxMana: number;
  isPlayer: boolean;
}

interface VisualCombatArenaProps {
  player: CombatEntity;
  enemies: CombatEntity[];
  damageNumbers: Array<{ id: string; amount: number; x: number; y: number }>;
  onEntityClick: (id: string) => void;
}

export const VisualCombatArena: React.FC<VisualCombatArenaProps> = ({
  player,
  enemies,
  damageNumbers,
  onEntityClick
}) => {
  return (
    <div className="relative w-full h-96 bg-gradient-to-b from-purple-900/20 to-black rounded-lg border border-purple-500/30 overflow-hidden">
      {/* Player Side - NOT TARGETABLE */}
      <div className="absolute bottom-8 left-8">
        <CombatEntityCard
          entity={player}
          onClick={() => {}}
          position="left"
          isTargetable={false}
        />
      </div>

      {/* Enemy Side - TARGETABLE */}
      <div className="absolute top-8 right-8 space-y-4">
        {enemies.map((enemy, idx) => (
          <CombatEntityCard
            key={enemy.id}
            entity={enemy}
            onClick={() => enemy.hp > 0 && onEntityClick(enemy.id)}
            position="right"
            style={{ marginTop: idx * 120 }}
            isTargetable={enemy.hp > 0}
          />
        ))}
      </div>

      {/* Damage Numbers */}
      <AnimatePresence>
        {damageNumbers.map(damage => (
          <motion.div
            key={damage.id}
            initial={{ y: damage.y, x: damage.x, opacity: 1, scale: 1 }}
            animate={{ y: damage.y - 60, opacity: 0, scale: 1.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            className="absolute text-2xl font-bold text-red-400"
            style={{ left: damage.x, top: damage.y }}
          >
            -{damage.amount}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

interface CombatEntityCardProps {
  entity: CombatEntity;
  onClick: () => void;
  position: 'left' | 'right';
  style?: React.CSSProperties;
  isTargetable?: boolean;
}

const CombatEntityCard: React.FC<CombatEntityCardProps> = ({
  entity,
  onClick,
  position,
  style,
  isTargetable = true
}) => {
  const hpPercent = (entity.hp / entity.maxHp) * 100;
  const manaPercent = (entity.mana / entity.maxMana) * 100;
  const isLowHp = hpPercent < 30;

  return (
    <motion.div
      className={`${isTargetable ? 'cursor-pointer' : 'cursor-default'} ${position === 'left' ? 'ml-0' : 'mr-0'}`}
      style={style}
      onClick={isTargetable ? onClick : undefined}
      whileHover={isTargetable ? { scale: 1.05 } : {}}
      whileTap={isTargetable ? { scale: 0.95 } : {}}
    >
      <div className={`p-4 rounded-lg border-2 ${
        entity.isPlayer
          ? 'bg-cyan-900/30 border-cyan-400'
          : 'bg-red-900/30 border-red-400'
      } backdrop-blur-sm min-w-[200px]`}>
        <h3 className="text-lg font-bold mb-2">{entity.name}</h3>

        {/* Health Bar */}
        <div className="mb-2">
          <div className="flex justify-between text-xs mb-1">
            <span>HP</span>
            <span>{entity.hp}/{entity.maxHp}</span>
          </div>
          <div className="h-4 bg-gray-800 rounded-full overflow-hidden">
            <motion.div
              className={`h-full ${isLowHp ? 'bg-red-500' : 'bg-green-500'}`}
              initial={{ width: `${hpPercent}%` }}
              animate={{ width: `${hpPercent}%` }}
              transition={{ type: "spring", stiffness: 100 }}
            />
          </div>
        </div>

        {/* Mana Bar */}
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span>Mana</span>
            <span>{entity.mana}/{entity.maxMana}</span>
          </div>
          <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-blue-500"
              initial={{ width: `${manaPercent}%` }}
              animate={{ width: `${manaPercent}%` }}
              transition={{ type: "spring", stiffness: 100 }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};
